/**
 * 局部「轻等待闸」——只用于次要入口（/share /report /article），首页永不包裹。
 *
 * 当运营把档位切到 queue 时：访问这些次要页会先看到一个「排队 · 自动重试」
 * 的等待页，倒计时结束后自动进入真实内容（不要求用户手动刷新、更不提示「多刷几遍」）。
 * 一旦本会话过闸一次，后续不再等待，避免反复打扰。
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { usePulse } from '@/lib/pulse'
import { track } from '@/lib/track'

const PASSED_KEY = 'zhcz_queued_ok'
const WAIT_SECONDS = 6

function alreadyPassed(): boolean {
  try {
    return sessionStorage.getItem(PASSED_KEY) === '1'
  } catch {
    return false
  }
}

export function QueueGate({ children }: { children: ReactNode }) {
  const pulse = usePulse()
  const [passed, setPassed] = useState(alreadyPassed)

  // pulse 未知（加载中）或非 queue 档：直接放行，保证正常访客零影响
  if (passed || !pulse || pulse.mode !== 'queue') {
    return <>{children}</>
  }
  return <WaitScreen onDone={() => setPassed(true)} />
}

function WaitScreen({ onDone }: { onDone: () => void }) {
  const [left, setLeft] = useState(WAIT_SECONDS)
  const firedRef = useRef(false)

  useEffect(() => {
    track('queue_wait', { seconds: WAIT_SECONDS })
    const id = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          clearInterval(id)
          if (!firedRef.current) {
            firedRef.current = true
            try {
              sessionStorage.setItem(PASSED_KEY, '1')
            } catch {
              /* ignore */
            }
            // 让进度条走到 100% 再进入，观感更顺
            setTimeout(onDone, 250)
          }
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [onDone])

  const pct = Math.round(((WAIT_SECONDS - left) / WAIT_SECONDS) * 100)

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-100 via-brand-50 to-paper-50 flex flex-col items-center justify-center px-6 py-12 text-center">
      <img src="/logo_1.png" alt="方法星球" className="h-12 w-12 rounded-xl shadow-soft mb-6" />
      <div className="text-3xl mb-3" aria-hidden="true">⏳</div>
      <h1 className="text-h2 text-ink-900 mb-2 max-w-xs">当前访问高峰，正在为你排队…</h1>
      <p className="text-body text-ink-600 max-w-sm leading-relaxed">
        出分高峰期访问的家长较多，系统正在分批放行。
        <br />
        预计 <span className="font-semibold text-brand-700 tabular-nums">{left}</span> 秒后自动进入，无需手动刷新。
      </p>

      <div className="mt-7 w-full max-w-xs h-2 rounded-full bg-white/70 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-brand-400 to-brand-700 transition-all duration-1000 ease-linear"
          style={{ width: `${pct}%` }}
        />
      </div>

      <p className="mt-8 text-sm text-ink-400">方法星球 · 邹城中考志愿测算</p>
    </div>
  )
}
